// PLAN-CHECK: the memory check a DE ran before it asked for this approval
// (`ApprovalProposal.plan_check`, set by the server from the DE's latest `plan_check` of its run).
// A conflict is said loudly: memory holds a decision or rule this plan goes against.
import { Badge, InfoTip } from "@companyos/ui";
import { formatDateTime } from "../format";

export type PlanCheck = {
  verdict: "conflict" | "caution" | "no_conflict_found" | "unjudged" | "unavailable";
  plan: string | null;
  conflicts: { type: string; title: string }[];
  checked_at: string | null;
};

const LABEL: Record<PlanCheck["verdict"], string> = {
  conflict: "Memory: conflict",
  caution: "Memory: caution",
  no_conflict_found: "Memory: no conflict",
  unjudged: "Memory: not judged",
  unavailable: "Memory: not checked",
};

const VARIANT = {
  conflict: "danger",
  caution: "warning",
  no_conflict_found: "success",
  unjudged: "neutral",
  unavailable: "neutral",
} as const;

export function PlanCheckBadge({ check }: { check: PlanCheck | null | undefined }) {
  if (!check) return null;
  return (
    <span
      className="inline-flex items-center gap-1"
      data-slot="plan-check"
      data-verdict={check.verdict}
    >
      <Badge variant={VARIANT[check.verdict]}>{LABEL[check.verdict]}</Badge>
      <InfoTip label="About the memory check">
        <span className="flex flex-col gap-1">
          <span>
            The agent checked its plan against past decisions and rules before asking
            {check.checked_at ? ` (${formatDateTime(check.checked_at)})` : ""}.
          </span>
          {check.plan ? <span>Plan: {check.plan}</span> : null}
          {check.conflicts.length ? (
            <span>
              Goes against: {check.conflicts.map((c) => `${c.title} (${c.type})`).join("; ")}
            </span>
          ) : null}
        </span>
      </InfoTip>
    </span>
  );
}
